// Components==============
import storeWrongAnswer from 'actions/wrongAnswer/store';
import { motion } from 'framer-motion';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { HomeworkContext } from 'screens/Homework/Homework';
import { convertString } from 'utilities/convertString';
import { useKeyboard } from 'utilities/useKeyboard';
import { ExamContext } from '../../Exam';
import './Bar.module.scss';
// =========================

const barVariants = {
    visible: {
        opacity: 1,
        y: 0,
    },
    hidden: {
        opacity: 0,
        y: 100,
    },
};

export default function Bar({
    sentence,
    index,
}: {
    sentence: string;
    index: number;
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState('');
    const [readOnly, setReadOnly] = useState(false);
    // Safari 3.0+ "[object HTMLElementConstructor]"
    const isSafari =
        /constructor/i.test(window.HTMLElement) ||
        (function (p) {
            return p.toString() === '[object SafariRemoteNotification]';
        })(
            !window.safari ||
                (typeof safari !== 'undefined' && safari.pushNotification)
        );

    const { sequence } = useContext(HomeworkContext);

    const {
        userAnswers,
        setUserAnswers,
        setCurrent,
        answer,
        setCanUpdateOnNextClick,
        fireHandleSubmit,
    } = useContext(ExamContext);

    const dispatch = useDispatch();
    const { treatmentId, homeworkId } = useParams() as {
        treatmentId: string;
        homeworkId: string;
    };

    function handleSubmit(e?: React.FormEvent<HTMLFormElement>) {
        e?.preventDefault();
        if (readOnly || !value) return;

        const givenAnswer = convertString(value);
        const correctAnswer = answer ? convertString(answer) : undefined;

        if (givenAnswer !== correctAnswer) {
            dispatch(
                storeWrongAnswer({
                    given_answer: givenAnswer,
                    correct_answer: correctAnswer,
                    metadata: {
                        sequence,
                    },
                    type: 'zin',
                    exercise_id: homeworkId,
                    exercise_type: 'homework',
                    treatment_id: treatmentId,
                })
            );
        }

        setReadOnly(true);
        setUserAnswers((prev) => [...prev, value]);

        setCurrent((prev) => prev + 1);
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        setValue(e.target.value);
        if (e.target.value.trim()) return setCanUpdateOnNextClick(true);
        setCanUpdateOnNextClick(false);
    }

    useEffect(() => {
        if (sentence === answer && fireHandleSubmit !== 0) handleSubmit();
    }, [fireHandleSubmit]);

    useEffect(() => {
        if (sentence === answer) inputRef.current?.focus();
    }, [answer]);

    useKeyboard({
        setValue,
        condition: !readOnly && sentence === answer,
        handleEnter: () => handleSubmit(),
    });

    return (
        <motion.form
            styleName="wrapper"
            variants={barVariants}
            animate={sentence === answer ? 'visible' : 'hidden'}
            initial="hidden"
            exit="hidden"
            onSubmit={handleSubmit}
            autoComplete="off"
        >
            <input
                ref={inputRef}
                type="text"
                name="word"
                value={userAnswers[index] || value}
                onChange={handleChange}
                readOnly={!!userAnswers[index] || readOnly}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                data-bwignore
            />
            {/* dirty fix to prevent auto fill from happening, except for Safari because then is shows the password suggestion inside the actual input  */}
            {isSafari ? null : (
                <input
                    type="password"
                    style={{
                        width: 0,
                        height: 0,
                        opacity: 0,
                        position: 'absolute',
                    }}
                />
            )}
        </motion.form>
    );
}
